import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Backdrop,
  Modal,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import DateTimePicker from "react-datetime-picker";
import Papa from "papaparse";
import { format, subMonths } from "date-fns";
import Divider from "@mui/material/Divider";
import LoadingButton from "@mui/lab/LoadingButton";
import CircularProgress from "@mui/material/CircularProgress";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import "react-datetime-picker/dist/DateTimePicker.css";
import "react-clock/dist/Clock.css";
import "react-calendar/dist/Calendar.css";
import dayjs from "dayjs";

import CustomSnackbar from "./CustomSnackbar";
import {
  getAllSettings,
  parseSettingRows,
  getCronJobsOrg,
  createCronJobOrg,
  deleteCronJobOrg,
  buildSyncJobOrg,
  countTotalRecords,
  buildGrabRecordsRequest,
  runRequest,
} from "../utils/api";
import {
  BORDER_LIGHT,
  BRAND_COLOR,
  BRAND_COLOR_DARK,
  BRAND_COLOR_TINT_SOFT,
  BUTTON_SHADOW,
  TEXT_ON_DARK,
  TEXT_SECONDARY,
} from "../utils/colors";

const RECORD_LIMIT = 20000;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  // width: 400,
  bgcolor: "background.paper",
  // border: "2px solid #000",
  boxShadow: 24,
  borderRadius: 5,
  p: 4,
};

const DataBackup = ({
  orgId,
  apiKey,
  connectionUrl,
  dataCenterUrl,
  zapiKey,
}) => {
  const [modules, setModules] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [time, setTime] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [total_records, set_total_records] = useState(null);
  const [total_pagination, set_total_pagination] = useState(null);
  const [chunks_of_records, set_chunks_of_records] = useState(null);
  //snackbar state and functions
  const [openSnack, setOpenSnack] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState(""); // success or error
  const [prevRunningSetting, setPrevRunningSetting] = useState();

  const [handleSubmitLoading, setHandleSubmitLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const handleOpenModal = () => {
    setOpenModal(true);
  };
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const auth = { orgId, apiKey };

  const showSnack = (snackMessage, snackSeverity) => {
    setMessage(snackMessage);
    setSeverity(snackSeverity);
    setOpenSnack(true);
  };

  // a listed v3 restore job is running — it deletes itself when finished
  const isRunningRestoreJob = (job) =>
    job?.execute_in === 1 && job?.body?.type === "restore";

  useEffect(() => {
    (async () => {
      const settingRows = await getAllSettings(auth);
      setModules(parseSettingRows(settingRows).map((row) => row.setting_schema.moduleInfo));

      const jobs = (await getCronJobsOrg(auth)) || [];
      const runningRestore = jobs.find(isRunningRestoreJob);
      if (runningRestore) {
        setPrevRunningSetting(runningRestore);
      }
    })();
  }, []);

  const downloadCSV = (args) => {
    let filename = args.filename || "export.csv";
    let columns = args.columns || null;

    let csv = Papa.unparse({ data: args.data, fields: columns });
    if (csv == null) return;

    var blob = new Blob([csv]);
    if (window.navigator.msSaveOrOpenBlob)
      // IE hack; see http://msdn.microsoft.com/en-us/library/ie/hh779016.aspx
      window.navigator.msSaveBlob(blob, args.filename);
    else {
      var a = window.document.createElement("a");
      a.href = window.URL.createObjectURL(blob, { type: "text/plain" });
      a.download = filename;
      document.body.appendChild(a);
      a.click(); // IE: "Access is denied"; see: https://connect.microsoft.com/IE/feedback/details/797361/ie-10-treats-blob-url-as-cross-origin-and-denies-access
      document.body.removeChild(a);
    }
    setLoading(false);
  };

  const csvChunkFilename = (index) =>
    `${selectedModule.api_name}_${format(time, "yyyy-MM-dd HH:mm:ss")}_${index + 1}.csv`;

  const handleModuleChange = (event, value) => {
    setSelectedModule(value);
    set_total_records(null);
    set_chunks_of_records(null);
  };

  /** Split the total record count into RECORD_LIMIT-sized download requests. */
  const buildRecordChunks = (totalRecords) => {
    const totalPagination = Math.ceil(totalRecords / RECORD_LIMIT);
    const chunksOfRecords = {};

    new Array(totalPagination).fill(null).forEach((_, index) => {
      const offset = index * RECORD_LIMIT;
      const limit = Math.min(RECORD_LIMIT, totalRecords - offset);

      chunksOfRecords[index] = {
        id: index,
        loading: false,
        apiRequest: buildGrabRecordsRequest(auth, {
          moduleName: selectedModule.api_name,
          timeRange: format(time, "yyyy-MM-dd HH:mm:ss"),
          limit,
          offset,
          connectionUrl,
        }),
      };
    });

    return { totalPagination, chunksOfRecords };
  };

  const handleFetchRecordsInfo = async () => {
    try {
      setLoading(true);
      const countResp = await countTotalRecords(auth, {
        moduleName: selectedModule.api_name,
        timeRange: format(time, "yyyy-MM-dd HH:mm:ss"),
        connectionUrl,
      });

      const totalRecords = Number(countResp.data[0].total_records);
      set_total_records(totalRecords);

      const { totalPagination, chunksOfRecords } = buildRecordChunks(totalRecords);
      set_chunks_of_records(chunksOfRecords);
      set_total_pagination(totalPagination);
    } catch (err) {
      showSnack(err, "error");
    } finally {
      setLoading(false);
    }
  };

  const setChunkLoading = (index, isLoading) => {
    set_chunks_of_records((prev) => ({
      ...prev,
      [index]: { ...prev[index], loading: isLoading },
    }));
  };

  const handleDownloadChunk = async (record, index) => {
    try {
      setChunkLoading(index, true);
      const fetchedRecords = await runRequest(record.apiRequest);

      const csvData = fetchedRecords.data.data.map((indv) => indv.record_response);

      downloadCSV({
        filename: csvChunkFilename(index),
        data: csvData,
      });
    } catch (err) {
    } finally {
      setChunkLoading(index, false);
    }
  };

  const handleCronCreate = async () => {
    try {
      const resp = await createCronJobOrg(
        auth,
        buildSyncJobOrg({
          auth,
          dataCenterUrl,
          zapiKey,
          functionName: "restoremoduledata",
          body: {
            moduleName: selectedModule?.api_name,
            timeRange: dayjs(time).format("YYYY-MM-DD HH:mm:ss"),
            pageNo: 1,
            perPage: 1000,
            type: "restore",
            connectionUrl,
          },
          executeIn: 1,
        })
      );

      if (resp?.data && resp?.error === null) {
        setPrevRunningSetting({ job_id: resp?.data });
        showSnack("Successfully running..", "success");
      } else {
        showSnack("Something went wrong....", "error");
      }
    } catch (error) {
      showSnack(error.message, "error");
    }
  };

  const handleCronUpdate = async () => {
    try {
      setHandleSubmitLoading(true);
      // v3 jobs have no "Stopped" state — removing the job stops the loop
      const resp = await deleteCronJobOrg(auth, prevRunningSetting?.job_id);

      if (resp?.error === null) {
        setPrevRunningSetting();
        setHandleSubmitLoading(false);
        showSnack("Successfully Stopped", "success");
        handleCloseModal();
      } else {
        setHandleSubmitLoading(false);
        showSnack("Something went wrong. Try again later...!", "error");
      }
    } catch (error) {
      showSnack(error.message, "error");
      setHandleSubmitLoading(false);
    }
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnack(false);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <CustomSnackbar
        open={openSnack}
        handleClose={handleCloseSnack}
        message={message}
        severity={severity}
      />
      <Backdrop
        sx={{ color: TEXT_ON_DARK, zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6} lg={4.5}>
          <Typography variant="h5" mb={6}>
            Backup / Restore Data
          </Typography>

          <Grid
            container
            spacing={2}
            style={{ maxWidth: "500px", width: "100%" }}
            alignItems={"center"}
          >
            <Grid item xs={4}>
              <Typography style={{ fontWeight: 600 }}>Select Module</Typography>
            </Grid>
            <Grid item xs={8}>
              <Autocomplete
                id="modules-select"
                fullWidth
                size="small"
                options={modules || []}
                autoHighlight
                getOptionLabel={(option) => option.module_name}
                onChange={handleModuleChange}
                value={selectedModule}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                    }}
                    placeholder="Select Module"
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            style={{ maxWidth: "500px", width: "100%" }}
            alignItems={"center"}
            mt={0.5}
          >
            <Grid item xs={4}>
              <Typography style={{ fontWeight: 600 }}>Date</Typography>
            </Grid>
            <Grid item xs={8}>
              <DateTimePicker
                onChange={(value) => {
                  set_total_records(null);
                  set_chunks_of_records(null);
                  setTime(value);
                }}
                value={time}
                minDate={subMonths(new Date(), 1)}
                maxDate={new Date()}
                className={"datetimepicker"}
                style={{ width: "100%" }}
              />
            </Grid>
          </Grid>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              mt: 2,
            }}
          >
            <LoadingButton
              variant="contained"
              sx={{
                cursor: "pointer",
                backgroundColor: BRAND_COLOR,
                border:
                  selectedModule && time
                    ? `1px solid ${BRAND_COLOR}`
                    : `1px solid ${BORDER_LIGHT}`,
                boxShadow: BUTTON_SHADOW,
                "&:hover": {
                  backgroundColor: BRAND_COLOR_DARK,
                },
                textTransform: "capitalize",
              }}
              disabled={!selectedModule || !time}
              onClick={handleFetchRecordsInfo}
              size="small"
            >
              Fetch Records Information
            </LoadingButton>
          </Box>
        </Grid>
        <Grid item xs={12} md={6} lg={7.5}>
          {(() => {
            if (total_records === 0)
              return (
                <Typography variant="h5" mb={3}>
                  No Records Found
                </Typography>
              );
            return (
              total_records &&
              total_records > 0 && (
                <>
                  <Typography variant="h5" mb={3}>
                    Total Records Found: {total_records}
                  </Typography>
                  <Box
                    sx={{
                      border: `1px solid ${BORDER_LIGHT}`,
                      borderRadius: "8px",
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                    mb={2}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        Restore data in CRM
                      </Typography>
                      <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                        Restore {selectedModule.api_name} Records Data. Please
                        note that, if Records are deleted from{" "}
                        {selectedModule.api_name}, it will not restore the
                        record.
                      </Typography>
                    </Box>
                    {prevRunningSetting?.job_id ? (
                      <Button
                        variant="outlined"
                        sx={{
                          backgroundColor: "transparent",
                          boxShadow: BUTTON_SHADOW,
                          border:
                            selectedModule && time
                              ? `1px solid ${BRAND_COLOR}`
                              : `1px solid ${BORDER_LIGHT}`,
                          "&:hover": {
                            backgroundColor: BRAND_COLOR_TINT_SOFT,
                            border: `1px solid ${BRAND_COLOR}`,
                          },
                          color: BRAND_COLOR,
                          textTransform: "capitalize",
                        }}
                        onClick={handleOpenModal}
                        size="small"
                      >
                        Stop
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        sx={{
                          backgroundColor: "transparent",
                          boxShadow: BUTTON_SHADOW,
                          border:
                            selectedModule && time
                              ? `1px solid ${BRAND_COLOR}`
                              : `1px solid ${BORDER_LIGHT}`,
                          "&:hover": {
                            backgroundColor: BRAND_COLOR_TINT_SOFT,
                            border: `1px solid ${BRAND_COLOR}`,
                          },
                          color: BRAND_COLOR,
                          textTransform: "capitalize",
                          width: "120px",
                        }}
                        disabled={!selectedModule || !time}
                        onClick={handleCronCreate}
                        size="small"
                      >
                        Restore Data
                      </Button>
                    )}
                  </Box>
                  <Box
                    sx={{
                      border: `1px solid ${BORDER_LIGHT}`,
                      borderRadius: "8px",
                      p: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                      mb={2}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        Download in CSV
                      </Typography>
                    </Box>
                    <Divider />
                    <Box py={2}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Record Range
                      </Typography>
                    </Box>
                    <Divider />
                    {chunks_of_records &&
                      Object.values(chunks_of_records).map((record, index) => {
                        return (
                          <>
                            <Box
                              py={2}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography variant="body2">
                                {" "}
                                {record.apiRequest.data.offset + 1} -&nbsp;
                                {record.apiRequest.data.limit +
                                  record.apiRequest.data.offset}
                              </Typography>

                              {record.loading && <CircularProgress size={25} />}
                              {!record.loading && (
                                <FileDownloadOutlinedIcon
                                  sx={{ cursor: "pointer" }}
                                  onClick={() => handleDownloadChunk(record, index)}
                                />
                              )}
                            </Box>
                            {Object.values(chunks_of_records).length - 1 !==
                              index && <Divider />}
                          </>
                        );
                      })}
                  </Box>
                </>
              )
            );
          })()}
        </Grid>
      </Grid>
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Box
            sx={{
              bgcolor: "white",
              width: 500,
            }}
          >
            {" "}
            <Typography sx={{ mt: 4, fontWeight: "bold", fontSize: 16 }}>
              Do you want to stop this?
            </Typography>
            <Box
              sx={{
                mt: 3,
                ml: "1rem",
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                gap: "1rem",
              }}
            >
              <Button
                sx={{ width: 120 }}
                variant="outlined"
                onClick={handleCloseModal}
              >
                No
              </Button>

              <Button
                disabled={handleSubmitLoading}
                sx={{ width: 120 }}
                variant="contained"
                color="error"
                onClick={() => handleCronUpdate()}
              >
                {handleSubmitLoading ? (
                  <CircularProgress sx={{ color: "white", ml: 1 }} size={16} />
                ) : (
                  "Yes"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

export default DataBackup;
